import { api } from '../api';
import type { EntityComplianceProfilesOverview, EntityRow, Paginated } from '@/types/domain';

export interface EntityListParams {
  entityTypeId?: number;
  entityType?: string;
  contextId?: number;
  q?: string;
  limit?: number;
  cursor?: number;
  filters?: Record<string, string>;
}

function buildEntityQuery(params: EntityListParams): string {
  const search = new URLSearchParams();
  if (params.entityTypeId) search.set('entityTypeId', String(params.entityTypeId));
  if (params.entityType) search.set('entityType', params.entityType);
  if (params.contextId) search.set('contextId', String(params.contextId));
  if (params.q) search.set('q', params.q.slice(0, 256));
  if (params.limit) search.set('limit', String(params.limit));
  if (params.cursor) search.set('cursor', String(params.cursor));
  if (params.filters) {
    for (const [key, value] of Object.entries(params.filters)) {
      search.set(`filter[data.${key}]`, value.slice(0, 256));
    }
  }
  const qs = search.toString();
  return qs ? `/entities?${qs}` : '/entities';
}

export const entitiesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listEntities: builder.query<Paginated<EntityRow>, EntityListParams>({
      query: (params) => buildEntityQuery(params),
      providesTags: (result) => [
        ...(result?.items ?? []).map(({ id }) => ({ type: 'Entity' as const, id })),
        { type: 'Entity', id: 'LIST' },
      ],
    }),
    getEntity: builder.query<EntityRow, number>({
      query: (id) => `/entities/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Entity', id }],
    }),
    createEntity: builder.mutation<EntityRow, Record<string, unknown>>({
      query: (body) => ({ url: '/entities', method: 'POST', body }),
      invalidatesTags: [{ type: 'Entity', id: 'LIST' }],
    }),
    updateEntity: builder.mutation<EntityRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/entities/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Entity', id }, { type: 'Entity', id: 'LIST' }],
    }),
    deleteEntity: builder.mutation<void, number>({
      query: (id) => ({ url: `/entities/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Entity', id: 'LIST' }],
    }),
    assignEntityTags: builder.mutation<EntityRow, { id: number; tagIds: number[] }>({
      query: ({ id, tagIds }) => ({ url: `/entities/${id}/tags`, method: 'POST', body: { tagIds } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Entity', id }],
    }),
    assignEntityProfile: builder.mutation<
      { entityId: number; profileId: number },
      { entityId: number; profileId: number; contextId?: number | null }
    >({
      query: ({ entityId, profileId, contextId }) => ({
        url: `/entities/${entityId}/requirement-profiles`,
        method: 'POST',
        body: { profileId, ...(contextId !== undefined ? { contextId } : {}) },
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: entityId },
        { type: 'Entity', id: `compliance-profiles-${entityId}` },
      ],
    }),
    unassignEntityProfile: builder.mutation<
      void,
      { entityId: number; profileId: number; contextId?: number | null }
    >({
      query: ({ entityId, profileId, contextId }) => {
        const search = contextId != null ? `?contextId=${contextId}` : '';
        return {
          url: `/entities/${entityId}/requirement-profiles/${profileId}${search}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: entityId },
        { type: 'Entity', id: `compliance-profiles-${entityId}` },
      ],
    }),
    getEntityComplianceProfiles: builder.query<EntityComplianceProfilesOverview, number>({
      query: (entityId) => `/entities/${entityId}/compliance-profiles`,
      providesTags: (_r, _e, entityId) => [
        { type: 'Entity', id: `compliance-profiles-${entityId}` },
      ],
    }),
    listEntityAccessGrants: builder.query<EntityAccessGrantRow[], number>({
      query: (entityId) => `/entities/${entityId}/access-grants`,
      transformResponse: (response: { items: EntityAccessGrantRow[] }) => response.items,
      providesTags: (_r, _e, entityId) => [{ type: 'Entity', id: `access-grants-${entityId}` }],
    }),
    createEntityAccessGrant: builder.mutation<
      EntityAccessGrantRow,
      { entityId: number; body: CreateEntityAccessGrantBody }
    >({
      query: ({ entityId, body }) => ({
        url: `/entities/${entityId}/access-grants`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: `access-grants-${entityId}` },
        { type: 'Entity', id: 'LIST' },
      ],
    }),
    revokeEntityAccessGrant: builder.mutation<
      { revoked: true },
      { entityId: number; grantId: number }
    >({
      query: ({ entityId, grantId }) => ({
        url: `/entities/${entityId}/access-grants/${grantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: `access-grants-${entityId}` },
        { type: 'Entity', id: 'LIST' },
      ],
    }),
    listEntitySuperiorLinks: builder.query<EntitySuperiorLinksOverview, number>({
      query: (entityId) => `/entities/${entityId}/superior-links`,
      providesTags: (_r, _e, entityId) => [{ type: 'Entity', id: `superior-links-${entityId}` }],
    }),
    listTenantSuperiorLinks: builder.query<{ items: SuperiorLinkRow[] }, { limit?: number } | void>({
      query: (params) => {
        const limit = params?.limit ?? 500;
        return `/entities/superior-links?limit=${limit}`;
      },
      providesTags: [{ type: 'Entity', id: 'SUPERIOR_LINKS' }],
    }),
    createEntitySuperiorLink: builder.mutation<
      SuperiorLinkRow,
      { entityId: number; superiorEntityId: number }
    >({
      query: ({ entityId, superiorEntityId }) => ({
        url: `/entities/${entityId}/superior-links`,
        method: 'POST',
        body: { superiorEntityId },
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: `superior-links-${entityId}` },
        { type: 'Entity', id: 'SUPERIOR_LINKS' },
        { type: 'Entity', id: 'LIST' },
      ],
    }),
    revokeEntitySuperiorLink: builder.mutation<
      { revoked: true },
      { entityId: number; linkId: number }
    >({
      query: ({ entityId, linkId }) => ({
        url: `/entities/${entityId}/superior-links/${linkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: 'Entity', id: `superior-links-${entityId}` },
        { type: 'Entity', id: 'SUPERIOR_LINKS' },
      ],
    }),
    syncSuperiorLinks: builder.mutation<SyncSuperiorLinksResult, { dadorCodes?: string; transportistaCodes?: string }>({
      query: (body) => ({
        url: '/entities/superior-links/sync',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Entity', id: 'SUPERIOR_LINKS' },
        { type: 'Entity', id: 'LIST' },
      ],
    }),
  }),
});

export interface EntityAccessGrantRow {
  id: number;
  entityId: number;
  granteeKind: 'user' | 'entity' | 'role';
  granteeId: number;
  actions: string[];
  validFrom: string;
  validUntil: string | null;
  createdBy: number | null;
  createdAt: string;
  granteeLabel: string;
}

export interface CreateEntityAccessGrantBody {
  granteeKind: 'user' | 'entity' | 'role';
  granteeId: number;
  preset?: 'read_only' | 'view_and_upload' | 'edit_data';
  actions?: string[];
  validUntil?: string | null;
}

export interface SuperiorLinkRow {
  id: number;
  entityId: number;
  superiorEntityId: number;
  validFrom: string;
  validUntil: string | null;
  entityLabel: string;
  superiorLabel: string;
}

export interface EntitySuperiorLinksOverview {
  superiors: SuperiorLinkRow[];
  subordinates: SuperiorLinkRow[];
}

export interface SyncSuperiorLinksResult {
  inferred: number;
  created: number;
  existing: number;
  previousVisibilityMode: string | null;
}

export const {
  useListEntitiesQuery,
  useGetEntityQuery,
  useCreateEntityMutation,
  useUpdateEntityMutation,
  useDeleteEntityMutation,
  useAssignEntityTagsMutation,
  useAssignEntityProfileMutation,
  useUnassignEntityProfileMutation,
  useGetEntityComplianceProfilesQuery,
  useListEntityAccessGrantsQuery,
  useCreateEntityAccessGrantMutation,
  useRevokeEntityAccessGrantMutation,
  useListEntitySuperiorLinksQuery,
  useListTenantSuperiorLinksQuery,
  useCreateEntitySuperiorLinkMutation,
  useRevokeEntitySuperiorLinkMutation,
  useSyncSuperiorLinksMutation,
} = entitiesApi;
